/**
 * ICM "Date Filter" — the shared 1M / 3M / 6M / 1Y / 2Y / 5Y / ALL preset picker
 * legacy puts on Item Requisition, Ordering Items, Receiving Items, Item
 * Distribution and Item Consumption.
 *
 * Legacy builds it as a ChoiceAction with exactly these seven items
 * (ConsumptionViewController ~115-131 and the matching *DateFilter actions on
 * the other controllers) and applies it as
 *     DateDiffMonth(<the form's date column>, Now()) <= N
 * with ALL meaning "no date filter".  The month count travels to the SP as
 * `months` (null = ALL), and each SP applies it to its OWN date column —
 * ConsumptionDate, ReceiveDate, RequestedDate and so on — which is why the
 * column is chosen server-side rather than named here.
 *
 * ONE definition for every ICM form (standing rule 8): Ordering Items had its
 * own private copy of this list, which is now imported from here.
 */

export type DateFilterId = '1M' | '3M' | '6M' | '1Y' | '2Y' | '5Y' | 'ALL';

export const DATE_FILTERS: Array<{ id: DateFilterId; label: string; months: number | null }> = [
  { id: '1M',  label: '1M',  months: 1 },
  { id: '3M',  label: '3M',  months: 3 },
  { id: '6M',  label: '6M',  months: 6 },
  { id: '1Y',  label: '1Y',  months: 12 },
  { id: '2Y',  label: '2Y',  months: 24 },
  { id: '5Y',  label: '5Y',  months: 60 },
  { id: 'ALL', label: 'ALL', months: null },
];

/**
 * The window every ICM list opens on (owner decision 2026-10-01: "by default,
 * set the Date Filter as 3M").
 *
 * ONE constant rather than a literal per page: the seven screens had drifted to
 * three different defaults (ALL on four, 1M on two, 3M on one), so the same
 * control meant a different thing depending on which form you opened. Changing
 * the lab's default is now one edit here.
 */
export const DEFAULT_DATE_FILTER: DateFilterId = '3M';

/**
 * ...and the window a WORKING QUEUE opens on — the SAME 3M (owner decision
 * 2026-10-06, annotated on the Requisition Review screenshot).
 *
 * ⚠️ THIS REVERSES THE 2026-10-01 DECISION, knowingly. That day the queue was
 * moved to ALL because a 3M default showed an empty Requisition Review: the 28
 * requisitions awaiting review are all older than the window, so the screen read
 * "no data" while real work was outstanding (owner report).
 *
 * It is reversed because 3M is what GoLive does. Legacy takes the default from
 * `DefaultSetting.InventoryWorkFlow` and applies it to every view the chip
 * targets, the pending queues included (RequisitionViewcontroller :188-240) —
 * and on this database the one row carrying a value is 1 = ThreeMonth, verified
 * 2026-10-06. (An earlier version of this comment said "0 = 1M on this
 * database"; that was wrong — 9,061 of the 9,062 DefaultSetting rows are 0 and
 * the one that matters is 1.) So GoLive opens that queue empty too.
 *
 * ⚠️ THE CONSEQUENCE IS REAL AND MEASURED: Requisition Review opens on 0 of its
 * 28 pending rows, because the newest is 20 April 2026 and the 3M window starts
 * 1 July. Switching the chip to 1Y shows 16, ALL shows all 28. Item Requisition's
 * own queue is unaffected — it is empty at every chip, having no drafts.
 *
 * Reading DefaultSetting.InventoryWorkFlow at runtime rather than hardcoding the
 * chip is the proper end state: it would track the lab's own setting instead of
 * a constant that has now been flipped twice. Tracked, not done.
 */
export const WORKING_DATE_FILTER: DateFilterId = DEFAULT_DATE_FILTER;

/** The default for a view: history narrows, a pending queue shows everything. */
export function defaultDateFilter(isHistory: boolean): DateFilterId {
  return isHistory ? DEFAULT_DATE_FILTER : WORKING_DATE_FILTER;
}

/** The month count a preset stands for — null for ALL (no date filter). */
export function monthsOf(id: DateFilterId): number | null {
  return DATE_FILTERS.find(d => d.id === id)?.months ?? null;
}

/**
 * The picker itself, labelled as legacy labels it.  Drop it in an `<AppToolbar>`
 * `left` slot; it carries no state of its own.
 */
export function DateFilterSelect({
  value, onChange, title = 'Date Filter',
}: {
  value: DateFilterId;
  onChange: (id: DateFilterId) => void;
  title?: string;
}) {
  return (
    <span style={{ marginLeft: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 12 }}>{title}</span>
      <select
        className="sd-input"
        style={{ width: 90 }}
        value={value}
        title="Limit the list to the last N months (legacy Date Filter)"
        onChange={e => onChange(e.target.value as DateFilterId)}
      >
        {DATE_FILTERS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
      </select>
    </span>
  );
}
